/**
 * Single source of truth for the API base URL.
 * All components should import { API } from this file.
 *
 * Reads REACT_APP_BACKEND_URL which is baked into the bundle at build time.
 * The deployment workflow selects the production or preview origin at build
 * time. Components must use this module rather than hard-coding an origin.
 *
 * Usage: `${API}/bonsai/mail/send`
 *
 * Side effect: registers a global axios request interceptor that injects the
 * X-Bonsai-User-Id session header on every outbound request when a user id is
 * available in sessionStorage / localStorage. Importing this module from
 * src/index.js guarantees the interceptor is attached before any component
 * mounts, so route handlers post-#340 (which require the session header) all
 * succeed without each call having to wire up its own headers.
 */
import axios from "axios";
import { isClientFacingPath, CLIENT_LINK_EXPIRED_EVENT } from "./clientFacingRoutes";
// Re-exported so existing callers and tests that import the client-path
// helper from config/api keep working; the list itself lives in one place.
export { isClientFacingPath, CLIENT_FACING_PATH_PREFIXES } from "./clientFacingRoutes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// BM-C / AUTH-BM-1: send the httponly `bonsai_session` cookie on every request.
// Identity now travels in an unforgeable signed cookie (minted by the backend
// at /bonsai/auth/callback). withCredentials is required so the browser
// attaches the cookie to XHR/fetch. Set once here, globally, before any
// component mounts.
axios.defaults.withCredentials = true;

const readBonsaiUserId = () => {
  try {
    return (
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") ||
      ""
    );
  } catch {
    return "";
  }
};

/**
 * authFetch — drop-in replacement for window.fetch that injects the same
 * ``X-Bonsai-User-Id`` header the axios interceptor injects, and sets
 * ``credentials: 'include'`` so the signed ``bonsai_session`` cookie also
 * travels.
 *
 * Use this instead of raw ``fetch()`` for every internal API call.
 * Raw ``fetch()`` bypasses the axios interceptor and lands anonymous — the
 * whole /policy/* surface shipped that way and 401'd every request.
 *
 * Passes through the standard fetch API surface (url, init) — no signature
 * change. Existing headers on the caller win over the injected header, so a
 * caller can still override in the rare case where a different identity is
 * needed.
 */
export async function authFetch(input, init = {}) {
  const userId = readBonsaiUserId();
  const headers = new Headers(init.headers || {});
  if (userId && !headers.has("X-Bonsai-User-Id")) {
    headers.set("X-Bonsai-User-Id", userId);
  }
  return fetch(input, {
    ...init,
    credentials: init.credentials || "include",
    headers,
  });
}

let interceptorRegistered = false;
let sessionExpiredHandled = false;

// Fix 6 — keys cleared on session expiry. Kept narrow on purpose so we
// don't blow away unrelated localStorage state (drafts, ui prefs).
const _AUTH_KEYS_TO_CLEAR = [
  "bonsai_user_id",
  "bonsai_user_email",
  "bonsai_auth",
  "bonsai_last_activity",
  "user",
  "token",
];

function _clearAuthState() {
  try {
    for (const k of _AUTH_KEYS_TO_CLEAR) {
      try { sessionStorage.removeItem(k); } catch {}
      try { localStorage.removeItem(k); } catch {}
    }
  } catch {}
}

/**
 * Where a SESSION_EXPIRED 401 should send the browser, or null to stay put.
 *
 * BM-H604: never /login on a client-facing page (see clientFacingRoutes.js),
 * and never from /login itself. Exported so the rule is tested directly.
 */
export function sessionExpiredRedirectTarget(pathname) {
  const p = typeof pathname === "string" ? pathname : "";
  if (isClientFacingPath(p)) return null;
  if (p.startsWith("/login")) return null;
  return "/login?session_expired=1";
}

export function ensureBonsaiAuthInterceptor() {
  if (interceptorRegistered) return;
  axios.interceptors.request.use((config) => {
    // BM-C: the signed `bonsai_session` cookie (sent via withCredentials) is
    // now the primary credential. The X-Bonsai-User-Id header is kept ONLY as a
    // legacy fallback for the deploy transition window — until every user has
    // logged in once and holds a cookie. The server treats the cookie as
    // authoritative (a forged header is overwritten by the verified cookie
    // identity), so keeping this send has no security downside and avoids
    // forcing a mass re-login on deploy. The localStorage/sessionStorage value
    // is a display/fallback hint, NEVER a primary auth source. Remove this
    // header send in a fast-follow once cookies are universal.
    const userId = readBonsaiUserId();
    if (userId) {
      config.headers = config.headers || {};
      if (!config.headers["X-Bonsai-User-Id"]) {
        config.headers["X-Bonsai-User-Id"] = userId;
      }
    }
    // Test-mode header. Directors flip this via the toggle on
    // /conflict-search (see components/TestModeToggle.jsx). When
    // sessionStorage.bonsai_test_mode === "1", every axios request
    // sends X-Bonsai-Test-Mode: 1, which the backend middleware
    // uses to reroute conflict-domain reads and writes to <name>_test
    // collections. Non-director accounts are rejected by the
    // backend with 403 even if the flag is somehow set locally.
    try {
      const testMode = (typeof window !== "undefined" && window.sessionStorage)
        ? window.sessionStorage.getItem("bonsai_test_mode")
        : null;
      if (testMode === "1") {
        config.headers = config.headers || {};
        if (!config.headers["X-Bonsai-Test-Mode"]) {
          config.headers["X-Bonsai-Test-Mode"] = "1";
        }
      }
    } catch {
      // sessionStorage unavailable — skip silently.
    }
    return config;
  });
  // Fix 6 — global 401 → session-expired redirect when the backend
  // returned the structured ``code: "SESSION_EXPIRED"`` payload from
  // session_guard. We deliberately do NOT redirect on every 401: some
  // routes legitimately 401 on a per-request basis (e.g. a missing
  // matter-scoped header) without invalidating the global session.
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const status = error?.response?.status;
        const code = error?.response?.data?.detail?.code
          || error?.response?.data?.code;
        if (status === 401 && code === "SESSION_EXPIRED") {
          const pathname = (typeof window !== "undefined" && window.location)
            ? (window.location.pathname || "")
            : "";
          if (isClientFacingPath(pathname)) {
            // BM-H604: a client following an emailed link has no staff
            // session and must never be sent to the staff Microsoft login.
            // Leave any staff auth state alone (a colleague may be testing
            // the link in their own browser) and let the page show the
            // plain client message instead.
            try {
              window.dispatchEvent(new CustomEvent(CLIENT_LINK_EXPIRED_EVENT));
            } catch {
              // CustomEvent unavailable: the page's own error state stands.
            }
          } else if (!sessionExpiredHandled) {
            sessionExpiredHandled = true;
            _clearAuthState();
            const target = sessionExpiredRedirectTarget(pathname);
            if (target && typeof window !== "undefined" && window.location) {
              window.location.href = target;
            }
          }
        }
      } catch {
        // Never let interceptor logic break the original rejection.
      }
      return Promise.reject(error);
    },
  );
  interceptorRegistered = true;
}

ensureBonsaiAuthInterceptor();

/**
 * api — the axios instance that carries Bonsai identity.
 *
 * BM-H160. Importing this module is what registers the identity interceptor, so
 * a component that imports raw `axios` from the package only carries identity by
 * luck: it works if something else in the bundle happened to import this file
 * first, and lands anonymous if not. Importing `api` from here makes that
 * dependency structural instead of accidental, because you cannot get the
 * client without running the interceptor registration above.
 *
 * Use `import { API, api } from "../config/api"` and call `api.get(...)`.
 */
export const api = axios;

export default api;
